import React, { useEffect, useState, useMemo } from "react";
import { cn } from "@/lib/utils";
import { Command as CommandPrimitive } from "cmdk";
import { Search, Plus } from "lucide-react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import { membersStore } from "@/store/membersStore";
import { IMember } from "@/types/person";
import { AvatarAndTwoLineSkeleton } from "./skeletons";
import { searchInputStyles } from "./inputStyles";
import { AddMemberModal } from "./AddMemberModal";
import { MemberItem } from "./MemberItem";

interface MemberSelectorProps {
  onSelectedMemberChange?: (member: IMember) => void;
  placeholder?: string;
  className?: string;
}

export const MemberSelector: React.FC<MemberSelectorProps> = ({
  onSelectedMemberChange,
  placeholder = "Search members...",
  className,
}) => {
  const [searchValue, setSearchValue] = useState("");
  const [open, setOpen] = useState(false);
  const [addMemberModalOpen, setAddMemberModalOpen] = useState(false);
  const members = membersStore.members.use();
  const isLoading = membersStore.isLoading.use();

  // Debounced search effect
  useEffect(() => {
    if (searchValue.trim().length > 0) {
      const timeoutId = setTimeout(() => {
        membersStore.searchMember(searchValue);
      }, 300);

      return () => clearTimeout(timeoutId);
    } else {
      // Reset members list when search is empty
      membersStore.resetMembers();
    }
  }, [searchValue]);

  const reset = () => {
    setSearchValue("");
  };

  const onInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    // Close dropdown if clicking outside
    if (!e.relatedTarget?.hasAttribute("cmdk-list")) {
      setOpen(false);
    }
  };

  const onSelectItem = (member: IMember) => {
    onSelectedMemberChange?.(member);
    setSearchValue(
      `${member.firstName} ${member.secondName} ${member.lastName}`
        .replace(/\s+/g, " ")
        .trim()
    );
    setOpen(false);
  };

  const onAddNewMember = () => {
    setOpen(false);
    setAddMemberModalOpen(true);
  };

  return (
    <>
      <div className={cn("flex items-center w-full", className)}>
        <Popover open={open} onOpenChange={setOpen}>
          <Command shouldFilter={false}>
            <PopoverAnchor asChild>
              <div className="relative w-full">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400 pointer-events-none z-10" />
                <CommandPrimitive.Input
                  asChild
                  value={searchValue}
                  onValueChange={setSearchValue}
                  onKeyDown={(e) => setOpen(e.key !== "Escape")}
                  onMouseDown={() => setOpen((open) => !!searchValue || !open)}
                  onFocus={() => setOpen(true)}
                  onBlur={onInputBlur}
                >
                  <Input
                    placeholder={placeholder}
                    className={`${searchInputStyles} pl-12`}
                  />
                </CommandPrimitive.Input>
              </div>
            </PopoverAnchor>
            {!open && <CommandList aria-hidden="true" className="hidden" />}
            <PopoverContent
              asChild
              onOpenAutoFocus={(e) => e.preventDefault()}
              onInteractOutside={(e) => {
                if (
                  e.target instanceof Element &&
                  e.target.hasAttribute("cmdk-input")
                ) {
                  e.preventDefault();
                }
              }}
              className="w-[--radix-popover-trigger-width] p-0 border border-slate-200 shadow-lg rounded-lg bg-white"
            >
              <CommandList className="max-h-64 overflow-y-auto">
                {isLoading && (
                  <CommandPrimitive.Loading>
                    <div className="p-3 space-y-3">
                      <AvatarAndTwoLineSkeleton dark />
                      <AvatarAndTwoLineSkeleton dark />
                      <AvatarAndTwoLineSkeleton dark />
                    </div>
                  </CommandPrimitive.Loading>
                )}
                {members.length > 0 && !isLoading ? (
                  <CommandGroup>
                    {members.map((member) => (
                      <CommandItem
                        key={member.id}
                        value={member.id}
                        onMouseDown={(e) => e.preventDefault()}
                        onSelect={() => onSelectItem(member)}
                        className="flex items-center px-3 py-2.5 cursor-pointer hover:bg-slate-50 transition-colors duration-150 rounded-none first:rounded-t-lg"
                      >
                        <MemberItem member={member} />
                      </CommandItem>
                    ))}
                    <CommandItem
                      value="new-member"
                      onMouseDown={(e) => e.preventDefault()}
                      onSelect={onAddNewMember}
                      className="flex items-center px-3 py-2.5 cursor-pointer hover:bg-slate-50 transition-colors duration-150 rounded-none last:rounded-b-lg border-t border-slate-100"
                    >
                      <div className="flex items-center gap-3 text-slate-500">
                        <div className="h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center">
                          <Plus className="h-5 w-5" />
                        </div>
                        <span className="text-lg font-medium">Add New Member</span>
                      </div>
                    </CommandItem>
                  </CommandGroup>
                ) : null}
                {!isLoading ? (
                  <CommandEmpty className="py-6 text-center text-sm text-slate-500">
                    No members found
                  </CommandEmpty>
                ) : null}
              </CommandList>
            </PopoverContent>
          </Command>
        </Popover>
      </div>
      
      <AddMemberModal 
        open={addMemberModalOpen} 
        onOpenChange={setAddMemberModalOpen} 
      />
    </>
  );
};
